'use strict';
/* ==========================================================================
   levels/cards.js — step "cards" (orientation any): VIOLATION OR NOT? (DESIGN.md §5.2).

   An attempt = 8 scenario cards from content/bank.cards.json, drawn by the model (Rights.attempt('cards', json, {fresh}):
   easiest first, ≥ 2 private-actor and ≥ 2 accused-rights cards, the last attempt's cards avoided, the seed saved in
   Game.progress.bank.cards). Nothing here decides a verdict: each card carries verdict() (violation, right, rule, case),
   `vq` (Violation / No violation) and `rq` (the "Which right is involved?" bank instance, options shuffled).

   Per card:  drop (a parchment index card lands on the desk: actor badge, the scenario in typewriter)
           →  vq: VIOLATION / NO VIOLATION. The truth is stamped on the card either way; a wrong answer gets the
              verdict's rule line (verdict().text)
           →  rq: "Which right is involved?", the 3 options with their badges (a freedom's badge, else the amendment's);
              a wrong pick is crossed out with the item's `why`, pick again
           →  verdict: the Judge's `explain` line, the right's badge pinned to the card, a docket slip under the card
              citing the case (name, year, vote from Rights.CASES) or the amendment's text, and the rule → next card.
   Stars: vq first try 1 + rq first try 1; of = 16, pass = bank.cards.json passStars (11). Game.progress.scores.cards =
   {best, last, of}; first answers under Game.progress.answers['cards/<key>/violation'] and ['cards/<key>'].
   The end card: the score, "Try again (new cards)" and "Back to the map" (a pass stamps the step: Game.complete).
   Resume: Game.progress.bank.cards.run = {i, ev, res}: a reload mid-attempt replays the same card's taps.

   Portrait phone (Dock.active): the card is the picture on the stage (with its stamp, badge and slip), the Judge beside
   it; the scenario text, the two verdict buttons, the right options and the Judge's line are dock widgets.
   Keys: 1 / V = Violation, 2 / N = No violation; 1–3 = a right; Enter / Space = next / start.

   QA hooks: sc.phase ('intro' | 'drop' | 'vq' | 'rq' | 'verdict' | 'end'), sc.i, sc.card (the instance), sc.cards,
   sc.seed, sc.attempt, sc.resumed, sc.results, sc.stars, sc.picks, sc.vPick, sc.say, sc.answer() → {violation (bool),
   vIndex, rightIndex}, sc.verdict(bool), sc.choose(i), sc.next(), sc.begin(fresh), sc.start(), sc.end(),
   sc.endButtons(), sc.finish() (tests: end now + stamp the step), sc.lay() (the card layout), Levels.cards.solution
   (the live scene's answer()), Levels.cards.preview(id) (swap the current card for a pool item: long-text screenshots).
   ========================================================================== */
(function () {
  const STEP = 'cards', TITLE = 'Violation or Not?';
  const DROP = 0.6, NEXT_GUARD = 0.3;
  const CV = () => window.Civic;
  const C = () => CV().C;
  const json = () => Game.content.bank_cards || { items: [], draw: {} };
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", TYPE = "'Special Elite'", HAND = "'Caveat Brush'";
  const INK = '#2b2420', PENCIL = '#5b6475', GREY = '#6f6a64', RULE_BLUE = 'rgba(70,110,170,0.28)';
  const ACTOR = {
    government: { label: 'GOVERNMENT', gov: true }, school: { label: 'PUBLIC SCHOOL', gov: true },
    police: { label: 'POLICE', gov: true }, court: { label: 'COURT', gov: true }, private: { label: 'PRIVATE', gov: false },
  };
  const sfx = (name, alt, o) => { const n = Audio.has(name) ? name : alt; if (n && Audio.has(n)) Audio.play(n, o); };
  const measure = (s, font) => { g.save(); g.font = font; const w = g.measureText(s).width; g.restore(); return w; };
  const round = (v, k) => Math.round(v / k) * k;

  /* a right's badge: the five freedoms use Civic.freedom (colour + icon); the other rights get a brown disc with the
     amendment number (in woodtype-ish slab). label: a printed tag under it. */
  const amendOf = id => { const a = Rights.RIGHTS[id].amendment; return Array.isArray(a) ? a.join('/') : String(a); };
  function rightBadge(id, x, y, size, { s = 1, label = false, seed = 61, t0 = -1e6, rot = 0 } = {}) {
    if (s <= 0.001 || !Rights.RIGHTS[id]) return;
    const Cv = CV(), c = C();
    if (Cv.FREEDOM[id]) { Cv.freedom(id, x, y, size, { s, label, seed, t0, rot }); return; }
    const r = Math.round(size * 0.5), num = amendOf(id);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperCircle(0, 0, r, '#5a4632', { seed, depth: 1 });
    inkCircle(0, 0, size * 0.43, { w: 2, color: 'rgba(243,234,215,0.55)', seed: seed + 1, passes: 1 });
    let fs = Math.round(size * (num.length > 2 ? 0.3 : 0.42));
    g.font = `${fs}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.cream;
    g.fillText(num, 0, size * 0.07);
    g.font = `${Math.round(size * 0.13)}px ${SLAB}`; g.fillText('AMEND.', 0, -size * 0.22);
    g.restore();
    if (label) {
      const ls = pop(T, t0, 0.3, 2.2) * s, txt = Rights.RIGHTS[id].short;
      if (ls <= 0.001) return;
      let f = Math.round(size * 0.17);
      g.save(); g.font = `${f}px ${SLAB}`;
      while (f > 12 && g.measureText(txt).width > size * 1.3) { f -= 1; g.font = `${f}px ${SLAB}`; }
      const w = round(g.measureText(txt).width + f * 0.9, 2), ty = y + size * 0.5 * s + f * 1.1;
      g.translate(x, ty); g.scale(ls, ls);
      paperRect(0, 0, w, Math.round(f * 1.5), '#5a4632', { seed: seed + 7, depth: 0.5, boil: false });
      g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.cream; g.fillText(txt, 0, f * 0.06);
      g.restore();
    }
  }

  /* the card on the desk: geometry + the scenario text fitted in typewriter (cached per card, UI scale, dock) */
  function layoutCard(cd, docked) {
    const K = docked ? { x: 790, top: 150, w: 1460, h: 860 } : { x: 650, top: 182, w: 1140, h: 640 };
    const left = K.x - K.w / 2, right = K.x + K.w / 2, bottom = K.top + K.h;
    const bs = docked ? 54 : Math.round(UI.text(34));                       // the actor badge's type
    const ruleY = K.top + Math.max(110, bs * 2.6);
    const tx = left + 70, tw = K.w - 140, tTop = ruleY + 34, tBot = bottom - Math.max(62, (docked ? 44 : UI.text(26)) * 2);
    const sMax = docked ? 88 : 62, sMin = docked ? 44 : Math.round(Math.max(30, UI.text(30)));
    let s = sMax, lines;
    for (; s >= sMin; s -= 2) {
      lines = UI._wrap(cd.text, tw, `${s}px ${TYPE}`);
      if (lines.length * s * 1.34 <= tBot - tTop && lines.every(l => measure(l, `${s}px ${TYPE}`) <= tw)) break;
    }
    if (s < sMin) { s = sMin; lines = UI._wrap(cd.text, tw, `${s}px ${TYPE}`); }
    const lh = s * 1.34, blockH = lines.length * lh;
    const y0 = tTop + Math.max(0, (tBot - tTop - blockH) * 0.35);
    return { ...K, left, right, bottom, bs, ruleY, tx, tw, tTop, tBot, s, lh, lines, y0, lineY: lines.map((_, j) => y0 + (j + 0.5) * lh),
      over: lines.map(l => measure(l, `${s}px ${TYPE}`) - tw).filter(v => v > 0.5), fits: y0 + blockH <= tBot + 0.5 };
  }

  class Cards extends Scene {
    get n() { return this.cards ? this.cards.length : 8; }
    get of() { return this.n * 2; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get card() { return this.cards ? this.cards[this.i] : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() { return this.answer(); }
    answer() { const cd = this.card; return cd ? { violation: cd.violation, vIndex: cd.answerViolation, rightIndex: cd.rq.answerIndex } : null; }

    enter() {
      this.phase = 'intro'; this.cards = null; this.i = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      Audio.play('paper', { gain: 0.5 });
      this.begin(false);
    }
    exit() { this.bankStamp(); }

    /* ---- the attempt (Rights.attempt: seeded, saved in Game.progress.bank.cards; + our run for resuming) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const P_ = Game.progress, S0 = P_.bank && P_.bank[STEP];
      const run = !fresh && S0 && S0.open && S0.run ? S0.run : null;
      const a = Rights.attempt(STEP, json(), { fresh });
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed; this.cards = a.cards;
      this.results = []; this.passed = false; this.collected = false; this.lays.clear();
      this.setup(0);
      if (a.resumed && run && this.restore(run)) return;
      this.phase = 'intro';
      if (fresh) this.start();                                                   // "Try again": straight to card 1
    }
    start() { if (this.cards && this.phase === 'intro') this.startCard(0); }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.cards.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.cardAt = Game.time - DROP - 1; this.phase = 'vq';
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 'v') this.verdict(v); else if (k === 'c') this.choose(v); }
        this.quiet = false;
        if (this.qAt) this.qAt = Math.min(this.qAt, Game.time);
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[cards] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.i = i; this.ev = []; this.vPick = null; this.picks = []; this.say = null;
      this.cardAt = Game.time; this.vAt = this.qAt = this.pickedAt = this.verdictAt = null;
      this._rc = this._vc = this._sl = null;
    }
    startCard(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.play('paper', 'whoosh', { gain: 0.55 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end' && this.phase !== 'intro') S.run = { i: this.i, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    play(name, alt, o) { if (!this.quiet) sfx(name, alt, o); }

    /* ---- step 1: Violation / No violation ---- */
    verdict(isViolation) {
      const cd = this.card;
      if (this.phase === 'drop') this.phase = 'vq';
      if (this.phase !== 'vq' || !cd) return false;
      const v = !!isViolation;
      this.ev.push(['v', v]);
      Game.progress.answer(STEP + '/' + cd.key + '/violation', v ? 'Violation' : 'No violation');
      this.vPick = v; this.vAt = Game.time; this.qAt = Game.time + 0.7;
      if (v === cd.violation) { this.say = null; this.play('gavel', 'stamp', { gain: 0.6 }); }
      else {
        this.say = { text: `${cd.violation ? 'It IS a violation' : 'No violation'}: ${cd.verdict.text}`, at: Game.time };
        this.play('gavel', 'stamp', { gain: 0.6 }); this.play('thunk', null, { gain: 0.35, rate: 0.9 });
      }
      this.phase = 'rq';
      this.save();
      return true;
    }
    /* ---- step 2: which right? ---- */
    choose(i) {
      const cd = this.card, q = cd && cd.rq;
      if (this.phase !== 'rq' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + cd.key, q.options[i]);
      this.picks.push(i); this.pickedAt = Game.time;
      if (Bank.check(q, i)) { this.play('ding', null, { gain: 0.6 }); this.toVerdict(); }
      else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toVerdict() {
      const cd = this.card, vs = this.vPick === cd.violation ? 1 : 0, rs = this.picks.length === 1 ? 1 : 0;
      this.phase = 'verdict'; this.verdictAt = Game.time;
      this.results[this.i] = { i: this.i, key: cd.key, violation: cd.violation, right: cd.right, vStar: vs, rStar: rs, stars: vs + rs,
        wrongPicks: this.picks.length - 1 };
      this.play('paper', null, { gain: 0.45 });
    }
    next() {
      if (this.phase !== 'verdict' || Game.time - this.verdictAt < NEXT_GUARD) return false;
      if (this.i < this.cards.length - 1) this.startCard(this.i + 1);
      else this.end();
      return true;
    }
    end() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) delete S.run;
      Rights.finish(STEP);                                                       // the next attempt draws new cards
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    finish() {
      if (this.done) return; this.done = true;
      if (!this.cards) this.begin(false);
      if (this.phase !== 'end') this.end();
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Back to the map ▶', go: () => this.collect() }, { label: 'Try again (new cards)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new cards)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    update() { if (this.phase === 'drop' && Game.time - this.cardAt > DROP + 0.1) this.phase = 'vq'; }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.start(); }
      else if (this.phase === 'drop' || this.phase === 'vq') { if (n === 1 || k === 'v' || k === 'V') this.verdict(true); else if (n === 2 || k === 'n' || k === 'N') this.verdict(false); }
      else if (this.phase === 'rq') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'verdict') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay() {
      const d = UI.docked(), key = this.i + '|' + (this.card && this.card.key) + '|' + d + '|' + Math.round(UI.text(30)) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { L = layoutCard(this.card, d); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.cards || this.phase === 'intro' || this.phase === 'end') return '';
      return `card ${this.i + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      UI.hud(TITLE, this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay();
      if (docked) this.drawJudge(1700, 1080, 620);
      this.drawCardOnDesk(L, docked);
      if (docked) return this.drawDock();
      if (this.phase === 'drop' || this.phase === 'vq') this.drawVQ();
      else if (this.phase === 'rq') this.drawRQ();
      else this.drawVerdict();
    }
    drawJudge(x, y, h) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('judge', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: 0 });
    }

    /* the index card: parchment, the actor badge, a red rule, blue ruled lines under the typewriter scenario; then the
       stamp (step 1 answered), the right's badge (step 2 solved) and the docket slip (the verdict) */
    drawCardOnDesk(L, docked) {
      const cd = this.card, Cv = CV(), c = C(), t0 = this.cardAt;
      const k = clamp((T - t0) / DROP), dy = -(1 - E.outCubic(k)) * 1150, rot = (1 - k) * -0.07 + Math.sin(k * Math.PI) * 0.015 + (docked ? 0 : -0.008);
      const cy = L.top + L.h / 2;
      g.save();
      g.translate(L.x, cy + dy); g.rotate(rot); g.translate(-L.x, -cy);
      Cv.parchment(L.x, cy, round(L.w, 10), round(L.h, 10), { seed: 520 + (this.i % 4), depth: 1.6, color: '#f4ead2' });
      // ruled lines (under the text lines) and the red header rule
      g.fillStyle = RULE_BLUE;
      for (let y = L.y0 + L.lh * 0.82; y < L.bottom - 30; y += L.lh) g.fillRect(L.left + 30, y, L.w - 60, 2);
      g.fillStyle = 'rgba(158,43,37,0.7)'; g.fillRect(L.left + 26, L.ruleY, L.w - 52, 3);
      // the actor badge
      const A = ACTOR[cd.actor] || ACTOR.government, bs = L.bs;
      g.save(); g.font = `${bs}px ${SLAB}`;
      const bw = round(g.measureText(A.label).width + bs * 1.3, 10), bh = round(bs * 1.7, 2);
      g.restore();
      const bx = L.left + 44 + bw / 2, by = L.top + (L.ruleY - L.top) / 2 + 2;
      paperRect(bx, by, bw, bh, A.gov ? c.navy : GREY, { seed: 540 + cd.actor.length, depth: 0.8, rot: -0.02, boil: false });
      g.save(); g.translate(bx, by); g.rotate(-0.02); g.font = `${bs}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.cream; g.fillText(A.label, 0, bs * 0.05); g.restore();
      // the scenario (typewriter)
      g.save(); g.font = `${L.s}px ${TYPE}`; g.fillStyle = INK; g.textAlign = 'left'; g.textBaseline = 'middle';
      L.lines.forEach((l, j) => g.fillText(l, L.tx, L.lineY[j]));
      // the card's number, bottom left
      const ns = docked ? 40 : Math.round(UI.text(26));
      g.font = `${ns}px ${TYPE}`; g.fillStyle = 'rgba(43,36,32,0.6)'; g.fillText(`No. ${this.i + 1} of ${this.n}`, L.left + 44, L.bottom - ns * 1.1);
      g.restore();
      // the stamp (the truth, once step 1 is answered)
      if (this.vAt != null) {
        const lbl = cd.violation ? 'VIOLATION' : 'NO VIOLATION', ss = docked ? 0.78 : 0.62;
        UI.stamp(lbl, L.right - (docked ? 380 : 300), by + 4, { rot: -0.09, s: pop(T, this.vAt, 0.3) * ss, color: cd.violation ? c.brick : c.navy });
      }
      g.restore();
      // the right's badge pinned to the card's corner (the verdict)
      if (this.phase === 'verdict') {
        const sz = docked ? 170 : 130;
        rightBadge(cd.right, L.right - (docked ? 10 : 50), L.top + 6, sz, { s: pop(T, this.verdictAt + 0.1, 0.35), label: true, t0: this.verdictAt + 0.3, seed: 560, rot: 0.08 });
        this.drawSlip(L, docked);
      }
    }
    /* the docket slip: the case (name, year, vote: Rights.CASES) or the amendment's words, then the rule */
    slipLines() {
      const cd = this.card, v = cd.verdict, R = Rights.RIGHTS[cd.right], cs = cd.case;
      const cite = cs ? `${cs.name} (${cs.year}, ${cs.vote.replace('-', '–')})` : `Amendment ${amendOf(cd.right)}: “…${R.text}”`;
      return { cite, rule: 'The rule: ' + v.text, isCase: !!cs };
    }
    slip(docked) {
      const key = this.i + '|' + docked + '|' + Math.round(UI.text(28));
      if (this._sl && this._sl.key === key) return this._sl;
      const S = this.slipLines(), L = this.lay();
      const w = docked ? 1300 : Math.min(1060, L.w - 60), cs = docked ? 46 : Math.round(UI.text(28)), rs = docked ? 42 : Math.round(UI.text(26));
      const citeF = `${cs}px ${S.isCase ? SLAB : TYPE}`, ruleF = `${rs}px ${TYPE}`;
      const cl = UI._wrap(S.cite, w - 130, citeF), rl = UI._wrap(S.rule, w - 130, ruleF), hs = docked ? 34 : Math.round(UI.text(22));
      const h = round(24 + hs * 1.3 + 8 + cl.length * cs * 1.2 + 8 + rl.length * rs * 1.25 + 22, 4);
      const x = L.x + (docked ? 30 : 60), y = Math.min(1080 - 10 - h / 2, L.bottom - 40 + h / 2);
      return (this._sl = { key, w: round(w, 10), h, x, y, cl, rl, cs, rs, hs, citeF, ruleF });
    }
    drawSlip(L, docked) {
      const Sl = this.slip(docked), c = C(), t0 = this.verdictAt + 0.35;
      const k = E.outBack(P(T, t0, t0 + 0.4), 1.2);
      if (k <= 0) return;
      const dx = (1 - k) * 600;
      g.save(); g.translate(Sl.x + dx, Sl.y); g.rotate(0.015);
      paperRect(0, 0, Sl.w, Sl.h, '#fbf5e6', { seed: 580, depth: 1.2, boil: false });
      const lft = -Sl.w / 2 + 40;
      g.fillStyle = 'rgba(158,43,37,0.45)'; g.fillRect(lft - 10, -Sl.h / 2 + 14, 2, Sl.h - 28);
      let y = -Sl.h / 2 + 24 + Sl.hs * 0.65;
      g.textAlign = 'left'; g.textBaseline = 'middle';
      g.font = `${Sl.hs}px ${TYPE}`; g.fillStyle = c.brick; g.fillText('DOCKET · ' + (this.card.case ? 'THE CASE' : 'THE TEXT'), lft + 14, y);
      y += Sl.hs * 0.65 + 8;
      g.font = Sl.citeF; g.fillStyle = INK;
      Sl.cl.forEach(l => { g.fillText(l, lft + 14, y + Sl.cs * 0.6); y += Sl.cs * 1.2; });
      y += 8;
      g.font = Sl.ruleF; g.fillStyle = '#3b332c';
      Sl.rl.forEach(l => { g.fillText(l, lft + 14, y + Sl.rs * 0.62); y += Sl.rs * 1.25; });
      g.restore();
    }

    /* ---- the right-hand panel (desktop / landscape) ---- */
    panelTop() { const hs = UI.hudSlot(0); return Math.max(150, hs.y + hs.r + 24); }
    drawPanel(x, y, w, h, seed) { CV().parchment(x + w / 2, y + h / 2, round(w, 10), round(h, 10), { seed, depth: 1.3 }); }
    drawVQ() {
      const X = 1290, W = 600, top = this.panelTop(), c = C();
      const qs = Math.round(UI.text(44)), bs = Math.round(UI.text(30));
      const qL = UI._wrap('Violation of a right?', W - 60, `${qs}px ${SLAB}`);
      const body = UI._wrap('Does this break the Bill of Rights? Look at who is doing it.', W - 60, `${bs}px ${SERIF}`);
      const h = 30 + qL.length * qs * 1.15 + 14 + body.length * bs * 1.3 + 26;
      this.drawPanel(X, top, W, h, 610);
      text(qL, X + 30, top + 30, qs * 1.15, `${qs}px ${SLAB}`, c.navy);
      text(body, X + 30, top + 30 + qL.length * qs * 1.15 + 14, bs * 1.3, `${bs}px ${SERIF}`, INK);
      const bh = UI.tap(124), gap = 28, y1 = Math.min(top + h + 34 + bh / 2, 1080 - 30 - bh * 1.5 - gap);
      const live = this.t > 0.2;
      const kv = UI.keys ? '1  ' : '', kn = UI.keys ? '2  ' : '';
      if (live && UI.button('cd-v' + this.i, kv + 'VIOLATION', X + W / 2, y1, { w: 540, h: 124, color: c.brick, size: 56, font: SLAB })) this.verdict(true);
      const low = y1 + bh * 1.5 + gap;
      if (low < 700) this.drawJudgeSmall(X + W - 150, 1080, Math.min(420, 1080 - low - 20));
      if (live && UI.button('cd-n' + this.i, kn + 'NO VIOLATION', X + W / 2, y1 + bh + gap, { w: 540, h: 124, color: c.navy, size: 50, font: SLAB })) this.verdict(false);
    }
    /* "Which right is involved?": the options as paper rows with their badges; the panel widens to the left over the
       card when a phone's big type doesn't fit */
    rqCard() {
      const key = this.i + '|' + this.picks.length + '|' + Math.round(UI.text(28));
      if (this._rc && this._rc.key === key) return this._rc;
      const q = this.card.rq, top0 = this.panelTop();
      const qs = Math.round(UI.text(40)), os = Math.round(UI.text(32)), ws = Math.round(UI.text(30)), ns = Math.round(UI.text(28));
      const last = this.picks[this.picks.length - 1], why = last != null && last !== q.answerIndex ? q.why[last] : '';
      let R;
      for (const X of [1290, 1150, 1000, 840, 680]) {
        const W = 1890 - X, badge = Math.round(Math.max(72, os * 2.1));
        const note = this.say ? UI._wrap(this.say.text, W - 60, `${ns}px ${HAND}`) : [];
        const qL = UI._wrap(q.text, W - 60, `${qs}px ${SLAB}`);
        const opts = q.options.map(o => UI._wrap(o, W - 90 - badge - 30, `${os}px ${SERIF}`));
        const rows = opts.map(ls => Math.max(UI.tap(96), badge + 16, ls.length * os * 1.25 + 26));
        const whyL = why ? UI._wrap(why, W - 60, `${ws}px ${HAND}`) : [];
        const nh = note.length ? note.length * ns * 1.15 + 14 : 0;
        const h = 26 + nh + qL.length * qs * 1.15 + 16 + rows.reduce((a, b) => a + b, 0) + 16 + whyL.length * ws * 1.15 + (whyL.length ? 16 : 0);
        const top = Math.max(96, Math.min(top0, 1070 - h));
        R = { key, X, W, badge, note, ns, nh, qL, qs, opts, os, rows, whyL, ws, h, top };
        if (top0 + h <= 1070) break;
      }
      return (this._rc = R);
    }
    drawRQ() {
      const R = this.rqCard(), { X, W, badge, note, ns, nh, qL, qs, opts, os, rows, whyL, ws, h, top } = R, c = C(), q = this.card.rq;
      if (T < this.qAt) return;
      this.drawPanel(X, top, W, h, 640);
      let y = top + 26;
      if (note.length) { note.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ns * 1.15, ns, this.say.at, { align: 'left', color: c.brick, seed: 650 + j, dur: 0.01 })); y += nh; }
      text(qL, X + 30, y, qs * 1.15, `${qs}px ${SLAB}`, c.navy); y += qL.length * qs * 1.15 + 16;
      opts.forEach((ls, i) => {
        const rh = rows[i], cy = y + rh / 2, crossed = this.picks.includes(i), rid = Rights.byName(q.options[i]);
        const hit = crossed ? { hover: false, clicked: false } : UI._hit('cd-opt' + this.i + '_' + i, X + W / 2, cy, W - 30, rh - 6);
        paperRect(X + W / 2, cy, round(W - 40, 10), round(rh - 10, 2), hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : PAL.cream, { seed: 660 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
        g.save(); if (crossed) g.globalAlpha *= 0.45;
        if (rid) rightBadge(rid, X + 30 + badge / 2, cy, badge * 0.9, { seed: 670 + i * 7 });
        g.restore();
        if (UI.keys) keyBadge(String(i + 1), X + 36, cy - badge * 0.38);
        const tx = X + 44 + badge, ty = cy - (ls.length - 1) * os * 1.25 / 2;
        text(ls, tx, ty - os * 0.625, os * 1.25, `${os}px ${SERIF}`, crossed ? PENCIL : INK);
        if (crossed) {
          const mw = Math.max(...ls.map(l => measure(l, `${os}px ${SERIF}`)));
          const a = i === this.picks[this.picks.length - 1] ? this.pickedAt : -1e9;
          ink([[tx - 6, cy + 10], [tx + 8 + mw, cy - 10]], { w: 5, color: c.brick, seed: 680 + i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      y += 16;
      whyL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ws * 1.15, ws, this.pickedAt + 0.2 + j * 0.2, { align: 'left', color: c.brick, seed: 690 + j }));
    }
    /* the Judge's card: verdict, his explain line, the stars, Next */
    verdictCard() {
      const key = this.i + '|' + Math.round(UI.text(28));
      if (this._vc && this._vc.key === key) return this._vc;
      const cd = this.card, r = this.results[this.i] || { stars: 0 }, top0 = this.panelTop(), bh = UI.tap(100);
      const S = { head: [Math.round(UI.text(46)), SLAB], who: [Math.round(UI.text(26)), TYPE], exp: [Math.round(UI.text(36)), HAND], stars: [Math.round(UI.text(34)), HAND] };
      const bits = [`verdict ${r.vStar}`, `right ${r.rStar}`];
      const F = [['head', cd.violation ? 'VIOLATION.' : 'NO VIOLATION.'], ['who', 'JUDGE O:'], ['exp', cd.explain], ['stars', `+${r.stars} star${r.stars === 1 ? '' : 's'} (${bits.join(', ')})`]];
      let R;
      for (const X of [1290, 1150, 1000]) {
        const W = 1890 - X;
        const rows = F.flatMap(([k, t]) => UI._wrap(t, W - 70, `${S[k][0]}px ${S[k][1]}`).map(l => [k, l]));
        const h = rows.reduce((a, [k]) => a + S[k][0] * 1.25, 0) + 50;
        const top = Math.max(96, Math.min(top0, 1080 - 14 - bh - 26 - h));
        R = { key, X, W, rows, h, S, bh, top };
        if (top0 + h + 26 + bh + 14 <= 1080) break;
      }
      return (this._vc = R);
    }
    drawVerdict() {
      const R = this.verdictCard(), { X, W, rows, h, S, bh, top } = R, c = C(), t0 = this.verdictAt + 0.2, cd = this.card;
      const by = top + h + 26 + bh / 2;
      // the Judge under his card when there is room (desktop)
      if (by + bh / 2 < 640) this.drawJudgeSmall(X + W - 150, 1080, Math.min(500, 1080 - (by + bh / 2) - 10), this.verdictAt);
      this.drawPanel(X, top, W, h, 700);
      let y = top + 26, tt = t0;
      const col = { head: cd.violation ? c.brick : c.navy, who: PENCIL, exp: INK, stars: c.navy };
      rows.forEach(([k, l]) => {
        const s = S[k][0], cy = y + s * 0.62;
        if (k === 'exp' || k === 'stars') hand(l, X + 34, cy, s, tt, { align: 'left', color: col[k], seed: 710 + Math.round(y) % 50, dur: 0.4 });
        else if (T >= tt) { g.save(); g.font = `${s}px ${S[k][1]}`; g.fillStyle = col[k]; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(l, X + 34, cy); g.restore(); }
        tt += 0.12; y += s * 1.25;
      });
      const label = this.i < this.n - 1 ? 'Next card ▶' : 'See my stars ▶';
      if (Game.time - this.verdictAt > NEXT_GUARD && UI.button('cd-next', label, X + W / 2, by, { w: 440, h: 100, color: c.brick, size: 48 })) this.next();
    }
    drawJudgeSmall(x, y, h, t0) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('judge', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: t0 != null && T - t0 < 2 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
    }

    /* ---- portrait: the card is the picture; everything to read and tap is in the dock ---- */
    drawDock() {
      const cd = this.card, q = cd.rq, id = this.attempt + '_' + this.i, A = ACTOR[cd.actor] || ACTOR.government;
      Dock.text('cd-scn' + id, [`${A.label}: ${cd.text}`], { cls: 'note' });
      const vOpts = ['VIOLATION', 'NO VIOLATION'];
      if (this.phase === 'drop' || this.phase === 'vq') {
        Dock.choices('cd-v' + id, { prompt: 'Violation of a right?', options: vOpts, state: ['', ''], onChoose: i => this.verdict(i === 0) });
        return;
      }
      const vi = this.vPick ? 0 : 1, right = cd.answerViolation;
      Dock.choices('cd-v' + id, { prompt: 'Violation of a right?', options: vOpts, locked: true,
        state: vOpts.map((o, i) => (i === right ? 'right' : i === vi ? 'wrong' : 'faded')) });
      if (this.say) Dock.text('cd-say' + id, [this.say.text], { cls: 'wrong', reveal: true });
      if (this.phase === 'rq') {
        const st = q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        const notes = q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : ''));
        Dock.choices('cd-r' + id, { prompt: q.text, options: q.options, state: st, notes, onChoose: i => this.choose(i) });
        return;
      }
      const r = this.results[this.i] || { stars: 0 }, S = this.slipLines();
      Dock.choices('cd-r' + id, { prompt: q.text, options: q.options, locked: true, state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')) });
      Dock.heading('cd-vh' + id, cd.violation ? 'Violation.' : 'No violation.');
      Dock.text('cd-exp' + id, ['Judge O: ' + cd.explain], { cls: 'note' });
      Dock.text('cd-dk' + id, [S.cite, S.rule], { cls: 'small' });
      Dock.text('cd-st' + id, [`+${r.stars} star${r.stars === 1 ? '' : 's'} (verdict ${r.vStar}, right ${r.rStar})`], { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.i < this.n - 1 ? 'Next card ▶' : 'See my stars ▶', label: 'next', aria: 'next card', go: true, onClick: () => this.next() });
    }

    introLines() {
      return [`${this.n} cases land on Judge O's desk.`, 'For each: is it a violation of a right? Then: which right is involved?',
        'Check the badge: who is doing it matters.', `${this.passStars} of ${this.of} stars to ratify it.`];
    }
    drawIntro(docked) {
      const t0 = this.enteredAt, c = C(), Cv = CV(), lines = this.introLines();
      this.drawJudge(docked ? 1600 : 330, 1080, docked ? 700 : 760);
      Cv.woodtype('VIOLATION OR NOT?', docked ? 760 : 1150, docked ? 420 : 250, docked ? 96 : 84, { color: c.cream, ink: c.navy, t0: t0 + 0.2, seed: 740 });
      if (docked) {
        Cv.tool('gavel', 760, 700, 260, { rot: -0.2, seed: 741 });
        Dock.heading('cd-intro', TITLE, { cls: 'big' });
        Dock.text('cd-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start the cards', go: true, color: 'red', onClick: () => this.start() });
        return;
      }
      const fs = Math.round(UI.text(38));
      const nL = lines.reduce((a, l) => a + UI._wrap(l, 1000, `${fs}px ${SERIF}`).length, 0);
      this.drawPanel(620, 340, 1060, nL * fs * 1.45 + 60, 745);
      let y = 400;
      lines.forEach((l, i) => UI._wrap(l, 1000, `${fs}px ${SERIF}`).forEach(tl => {
        if (T >= t0 + 0.5 + i * 0.3) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? c.brick : INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.3) / 0.3); g.fillText(tl, 1150, y); g.restore(); }
        y += fs * 1.45;
      }));
      if (this.t > 0.8 && UI.button('cd-start', 'Start ▶', 1150, Math.min(990, y + 80), { w: 420, h: 110, color: c.brick, size: 56 })) this.start();
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons(), c = C(), Cv = CV();
      const rows = this.cards.map((cd, i) => ({ r: this.results[i] || { stars: 0 }, cd }));
      const msg = this.passed ? 'Passed! Back to the map to ratify it.' : `Not yet: ${this.passStars} stars to ratify. New cards?`;
      if (docked) {
        Cv.woodtype(`${s} / ${of}`, 760, 380, 150, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 750 });
        UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 760, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6, color: c.brick });
        this.drawJudge(1640, 1080, 640);
        Dock.heading('cd-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('cd-end', [msg]);
        Dock.list('cd-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? c.navy : c.brick,
          lines: [x.cd.text, `${x.cd.violation ? 'Violation' : 'No violation'} · ${Rights.RIGHTS[x.cd.right].name} · ${x.r.stars} of 2 stars`] })));
        Dock.text('cd-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('cd-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Cv.woodtype(`${s} / ${of}`, 700, 200, 100, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 750 });
      UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 1340, 200, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.0, color: c.brick });
      const fs = Math.round(UI.text(28)), rh = Math.max(UI.touch() ? 60 : 62, fs * 1.55), bh = UI.tap(100), ch = (rows.length + 1) * rh + 50;
      const cardTop = Math.max(280, Math.min(300, 1080 - 30 - bh - 26 - ch)), X0 = 180, W = 1560;
      this.drawPanel(X0, cardTop, W, ch, 760);
      const bsz = Math.round(rh * 0.78);
      rows.forEach((x, i) => {
        const y = cardTop + 25 + (i + 0.5) * rh, right = Rights.RIGHTS[x.cd.right];
        rightBadge(x.cd.right, X0 + 40 + bsz / 2, y, bsz, { seed: 770 + i });
        let t = `${x.cd.violation ? 'VIOLATION' : 'NO VIOLATION'} · ${right.name}: ${x.cd.text}`;
        g.save(); g.font = `${fs}px ${SERIF}`;
        const maxW = W - 100 - bsz - 200;
        if (g.measureText(t).width > maxW) { while (g.measureText(t + '…').width > maxW && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        g.fillStyle = x.cd.violation ? c.brick : c.navy; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(t, X0 + 60 + bsz, y); g.restore();
        hand(`${x.r.stars} of 2`, X0 + W - 50, y, fs * 1.2, this.endAt + 0.4 + i * 0.08, { align: 'right', color: x.r.stars === 2 ? c.navy : c.brick, seed: 790 + i });
      });
      const py = cardTop + 25 + (rows.length + 0.5) * rh, by = Math.min(1080 - 24 - bh / 2, cardTop + ch + 26 + bh / 2);
      hand(msg, X0 + W / 2, py, Math.round(UI.text(38)), this.endAt + 0.8, { color: c.brick, seed: 799, dur: 0.5 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('cd-end' + i, b.label, 700 + i * 520, by, { w: 480, h: 100, color: i === 0 ? c.brick : c.navy, size: 42 })) b.go(); });
      hand(`quiz code ${this.seed}`, 1880, by - bh / 2 - 22, 28, this.endAt + 0.6, { align: 'right', color: PAL.ink, seed: 798 });
    }
  }

  /* small drawing helpers */
  function text(lines, x, yTop, lh, font, color) {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = 'left'; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  function keyBadge(txt, x, y) {
    g.save(); g.fillStyle = C().navy; g.beginPath(); g.arc(x, y, 16, 0, TAU); g.fill();
    g.font = `20px ${SLAB}`; g.fillStyle = C().cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, x, y + 1); g.restore();
  }

  Cards.STEP = STEP;
  Cards.layout = layoutCard;
  Cards.rightBadge = rightBadge;
  Object.defineProperty(Cards, 'solution', { get() { const s = Game.current(); return s instanceof Cards ? s.solution : null; } });
  /* tests / screenshots: swap the current card for the pool item `id` (same slot rule as the model) */
  Cards.preview = (id, seed = 1) => {
    const s = Game.current(); if (!(s instanceof Cards) || !s.cards) return false;
    const it = json().items.find(x => x.id === id); if (!it) return false;
    s.cards[s.i] = Rights.card(it, Bank.rng(seed), {});
    s.lays.clear(); s._rc = s._vc = s._sl = null;
    return true;
  };
  Levels.cards = Cards;
})();
